// pages/Support/SupportChat.jsx
// ─────────────────────────────────────────────────────────────
// Built from scratch around the unified Conversation model.
//
// OPEN-INSTANTLY FLOW: on mount, emits openConversation — backend
// either resumes an existing open conversation or creates a brand
// new "ai" one with zero messages. No enquiry form, no category
// picker gating the chat. The window is interactive the instant the
// server responds (typically near-instant on a live connection).
//
// HUMAN-TO-HUMAN ATTACHMENTS: file upload (image/audio/document)
// works identically whether the current responder is the AI or an
// agent — same uploadFile() path, same send_message shape. The AI
// can't interpret attachment CONTENT (see aiSupportService.js's
// toGroqMessages), but a human agent obviously can, so attachments
// matter most once a conversation reaches "with_agent" — kept fully
// enabled in all three active stages for consistency and because a
// user might reasonably want to share a screenshot as their very
// first message before the AI's even involved.
//
// NOTIFICATION SOUND integration: calls setSupportChatOpen(true) on
// mount, (false) on unmount — this is what lets
// useSupportNotifications (mounted globally, elsewhere) know not to
// play a sound while this component is actually visible.
// ─────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSocket } from "../../hooks/useSocket";
import { setSupportChatOpen } from "../../hooks/useSupportNotifications";

// FIXED — was hardcoded inline as "http://localhost:30032" at the
// uploadFile call site (a leftover dev-only URL). Restored as a
// proper top-of-file constant, env-driven, matching the same pattern
// useSocket.js already uses for the socket connection's URL.
const API_URL = import.meta.env.VITE_API_URL || "https://serve.easwallet.com";

export default function SupportChat({ user, onClose, closeIcon = "x" }) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [typing, setTyping] = useState(false);
  // FIXED — new. uploadFile previously caught a failed upload and
  // just removed the optimistic message + console.error'd — nothing
  // ever reached the UI, so a rejected upload looked identical to
  // doing nothing at all. This holds a short-lived error message
  // rendered just above the input bar (see the JSX near the bottom).
  const [uploadError, setUploadError] = useState(null);
  const [aiThinking, setAiThinking] = useState(false);
  const [opening, setOpening] = useState(true);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [recordingBlob, setRecordingBlob] = useState(null);

  const fileInputRef = useRef();
  const chatContainerRef = useRef();
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const typingTimerRef = useRef(null);
  const messageIdRef = useRef(0);
  const isTypingRef = useRef(false);

  const { connected, on, off, emit } = useSocket(user.userId);

  // FIXED — tell the global notification listener this chat is
  // visible, so it skips playing a sound while the user can already
  // see new messages arrive in-thread.
  useEffect(() => {
    setSupportChatOpen(true);
    return () => setSupportChatOpen(false);
  }, []);

  // Open (or resume) the conversation the moment the socket connects.
  useEffect(() => {
    if (!connected) return;
    emit("openConversation", { userId: user.userId });
  }, [connected, user.userId, emit]);

  useEffect(() => {
    function onConversationOpened(convo) {
      setConversation(convo);
      setMessages(convo.messages || []);
      setOpening(false);
    }
    function onReceiveMessage({ conversationId, message, tempId }) {
      setConversation(prev => (prev && prev._id === conversationId ? prev : prev));
      setAiThinking(false);
      setMessages(prev => {
        if (tempId) {
          const exists = prev.some(m => m._id === tempId);
          if (exists) return prev.map(m => (m._id === tempId ? { ...message, status: "sent" } : m));
        }
        if (prev.some(m => m._id === message._id)) return prev;
        return [...prev, { ...message, status: "sent" }];
      });
    }
    function onConversationEscalated({ conversationId, escalation }) {
      setConversation(prev => (prev && prev._id === conversationId ? { ...prev, status: "waiting_for_agent", escalation } : prev));
      setAiThinking(false);
    }
    // FIXED — was destructuring only { conversationId, agentId },
    // discarding the agent identity payload the backend now sends
    // (see socketHandlers.js's agentClaim — agentJoined now includes
    // `agent: { displayName, email }`). Stored on conversation.agent
    // so the header can display it.
    function onAgentJoined({ conversationId, agentId, agent }) {
      setConversation(prev => (prev && prev._id === conversationId ? { ...prev, status: "with_agent", assignedAgent: agentId, agent } : prev));
    }
    function onConversationClosed({ conversationId }) {
      setConversation(prev => (prev && prev._id === conversationId ? { ...prev, status: "closed" } : prev));
    }
    function onTyping() { setTyping(true); }
    function onStopTyping() { setTyping(false); }
    function onMessageError({ tempId }) {
      setMessages(prev => prev.filter(m => m._id !== tempId));
    }

    on("conversationOpened", onConversationOpened);
    on("receive_message", onReceiveMessage);
    on("conversationEscalated", onConversationEscalated);
    on("agentJoined", onAgentJoined);
    on("conversationClosed", onConversationClosed);
    on("typing", onTyping);
    on("stop_typing", onStopTyping);
    on("message_error", onMessageError);

    return () => {
      off("conversationOpened", onConversationOpened);
      off("receive_message", onReceiveMessage);
      off("conversationEscalated", onConversationEscalated);
      off("agentJoined", onAgentJoined);
      off("conversationClosed", onConversationClosed);
      off("typing", onTyping);
      off("stop_typing", onStopTyping);
      off("message_error", onMessageError);
    };
  }, [on, off]);

  useEffect(() => {
    chatContainerRef.current?.scrollTo({ top: chatContainerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing, aiThinking]);

  // FIXED — auto-dismiss the upload error after a few seconds, so it
  // doesn't sit there indefinitely if the user doesn't tap "Dismiss."
  // Still manually dismissible immediately via the button in the
  // banner itself.
  useEffect(() => {
    if (!uploadError) return;
    const timer = setTimeout(() => setUploadError(null), 6000);
    return () => clearTimeout(timer);
  }, [uploadError]);

  const handleInputChange = (e) => {
    setMessageInput(e.target.value);
    if (!isTypingRef.current && conversation) {
      isTypingRef.current = true;
      emit("typing", { conversationId: conversation._id, userId: user.userId });
    }
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      if (conversation) emit("stop_typing", { conversationId: conversation._id, userId: user.userId });
    }, 1500);
  };

  const sendMessage = () => {
    if (!messageInput.trim() || !conversation) return;
    clearTimeout(typingTimerRef.current);
    isTypingRef.current = false;
    emit("stop_typing", { conversationId: conversation._id, userId: user.userId });

    const tempId = `temp-${++messageIdRef.current}`;
    setMessages(prev => [...prev, { _id: tempId, role: "user", type: "text", text: messageInput, at: Date.now(), status: "sending" }]);
    setMessageInput("");
    if (conversation.status === "ai") setAiThinking(true);

    emit("send_message", {
      conversationId: conversation._id,
      role: "user",
      type: "text",
      text: messageInput,
      senderId: user.userId,
      tempId,
    });
  };

  const requestHuman = () => {
    if (!conversation) return;
    setAiThinking(false);
    emit("requestHumanAgent", { conversationId: conversation._id });
  };

  // FIXED — client-side size check, matching the backend's 10MB limit
  // (utils/chatUpload.js MAX_FILE_SIZE). Failing fast here means a
  // too-large file never round-trips to the server at all — no
  // optimistic bubble that has to be added then yanked back, no
  // waiting on a request that's guaranteed to be rejected.
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

  const uploadFile = async (file, msgType, duration) => {
    if (!conversation) return;

    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(`"${file.name}" is too large. Maximum size is 10MB.`);
      return;
    }

    setUploadError(null);
    const tempId = `temp-${++messageIdRef.current}`;
    setMessages(prev => [...prev, {
      _id: tempId, role: "user", type: msgType,
      text: msgType === "audio" ? "Voice Message" : file.name,
      at: Date.now(), status: "sending", duration: duration || undefined,
    }]);

    const fd = new FormData();
    fd.append("file", file);
    try {
      // FIXED — was hardcoded to http://localhost:30032, a leftover
      // dev URL that wouldn't even resolve outside the original
      // developer's machine. Uses the same VITE_API_URL constant this
      // file already defines for the socket connection.
      const res = await axios.post(`${API_URL}/api/mtrade/upload`, fd);

      // FIXED — was checking `if (!res.data?.status) throw`, which
      // assumed the backend's success response includes a literal
      // `status: true` field. The actual deployed response (confirmed
      // from a real upload) is:
      //   { url, type, name }
      // with NO status field at all — res.data.status is undefined,
      // which is falsy, so this check threw "Upload failed" on EVERY
      // upload, even fully successful ones. The file was correctly
      // saved and reachable at res.data.url the whole time; only the
      // frontend's own success check was wrong.
      //
      // FIXED to check for the actual thing that matters — a usable
      // url — rather than a status field whose presence depends on
      // which version of the upload controller happens to be
      // deployed. This works correctly whether or not a future
      // backend update adds a status field, since `res.data.url` is
      // the one thing every version of the response shape has had.
      if (!res.data?.url) {
        throw new Error(res.data?.message || "Upload failed — no file URL returned");
      }

      emit("send_message", {
        conversationId: conversation._id,
        role: "user",
        type: msgType,
        fileUrl: res.data.url,
        fileName: file.name,
        text: msgType === "audio" ? "Voice Message" : file.name,
        duration: duration || undefined,
        senderId: user.userId,
        tempId,
      });
      if (conversation.status === "ai") setAiThinking(true);
    } catch (err) {
      console.error(err);
      // FIXED — was silently removing the message + console.error
      // only. Now surfaces a real error to the user via uploadError,
      // rendered just above the input bar. err.response?.data?.message
      // is the clean message chatUpload.js's error handler now sends
      // (e.g. "File is too large. Maximum size is 10MB.") — falls back
      // to a generic message if the error didn't come from that path
      // (e.g. a network failure with no response at all).
      const message = err.response?.data?.message || err.message || "Upload failed. Please try again.";
      setUploadError(message);
      setMessages(prev => prev.filter(m => m._id !== tempId));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    let msgType = "document";
    if (file.type.startsWith("image/")) msgType = "image";
    if (file.type.startsWith("audio/")) msgType = "audio";
    uploadFile(file, msgType);
    e.target.value = "";
  };

  const toggleRecording = async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
        audioChunksRef.current = [];
        mediaRecorderRef.current.ondataavailable = (e) => audioChunksRef.current.push(e.data);
        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          setRecordingBlob(blob);
          setAudioPreviewUrl(URL.createObjectURL(blob));
          stream.getTracks().forEach(t => t.stop());
        };
        mediaRecorderRef.current.start();
        setIsRecording(true);
        setRecordingDuration(0);
        timerRef.current = setInterval(() => setRecordingDuration(p => p + 1), 1000);
      } catch {
        alert("Microphone access denied.");
      }
    }
  };

  const sendVoiceMessage = () => {
    if (!recordingBlob) return;
    uploadFile(recordingBlob, "audio", recordingDuration);
    setAudioPreviewUrl(null);
    setRecordingBlob(null);
    setRecordingDuration(0);
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) mediaRecorderRef.current.stop();
    clearInterval(timerRef.current);
    setIsRecording(false);
    setAudioPreviewUrl(null);
    setRecordingBlob(null);
    setRecordingDuration(0);
  };

  const renderMessage = (msg) => {
    const isUser = msg.role === "user";
    const isSystem = msg.role === "system";
    const isAi = msg.role === "ai";

    const rawTime = msg.at;
    const time = rawTime ? new Date(rawTime) : new Date();
    const timeString = isNaN(time.getTime()) ? "just now" : time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    if (isSystem) {
      return (
        <div key={msg._id} className="flex justify-center my-1">
          <span className="bg-surface-hover text-muted text-xs px-4 py-1.5 rounded-full">{msg.text}</span>
        </div>
      );
    }

    return (
      <div key={msg._id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
        {!isUser && (
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mr-2 mt-auto mb-1 flex-shrink-0"
            style={isAi ? { background: "var(--color-purple-light)", color: "var(--color-purple-text)" } : { background: "var(--color-brand-light)", color: "var(--color-brand-text)" }}>
            {isAi ? "🤖" : "A"}
          </div>
        )}
        <div className="max-w-[75%]">
          {!isUser && isAi && <p className="text-[10px] font-semibold mb-1 ml-1" style={{ color: "var(--color-purple-text)" }}>Easwallet Support</p>}
          <div className={`px-4 py-3 rounded-2xl text-sm shadow-sm ${isUser ? "rounded-br-sm" : "rounded-bl-sm border border-border"}`}
            style={{ background: isUser ? "var(--color-brand)" : "var(--color-surface)", color: isUser ? "#fff" : "var(--color-text-primary)" }}>
            {msg.type === "image" && <img src={msg.fileUrl} alt="" className="rounded-xl max-w-full mb-1" />}
            {msg.type === "audio" && (
              <div className="min-w-[200px]">
                <audio controls className="w-full h-8"><source src={msg.fileUrl} type="audio/webm" /></audio>
                {msg.duration && <p className="text-xs opacity-70 mt-1 text-right">{msg.duration}s</p>}
              </div>
            )}
            {msg.type === "document" && (
              <a href={msg.fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 underline underline-offset-2">📄 {msg.text}</a>
            )}
            {msg.type === "text" && <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>}
            <div className="text-[10px] mt-1.5 flex items-center gap-1 justify-end" style={{ opacity: isUser ? 0.75 : 1, color: isUser ? undefined : "var(--color-text-muted)" }}>
              {timeString}
              {isUser && <span>{msg.status === "sending" ? <span className="opacity-60">⋯</span> : <span style={{ color: "var(--gradient-hero-text-muted)" }}>✓✓</span>}</span>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const status = conversation?.status;

  return (
    <div className="flex flex-col flex-1 h-full bg-surface overflow-hidden">
      <div className="text-white px-5 py-4 flex items-center gap-3 flex-shrink-0" style={{ background: "var(--gradient-hero)" }}>
        {/* FIXED — back-arrow variant for full-page placement, vs the
            original X for modal/overlay placement. Rendered FIRST in
            the row (before the avatar) when acting as a back button,
            matching standard page-header conventions, instead of
            living at the trailing end like the modal's close X did. */}
        {onClose && closeIcon === "back" && (
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors flex-shrink-0">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
        )}
        <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center text-lg flex-shrink-0">
          {status === "with_agent" ? "🎧" : "🤖"}
        </div>
        <div className="min-w-0">
          {/* FIXED — was a hardcoded "Customer Support" string
              regardless of which agent actually joined. Now shows the
              agent's real display name (or email, falling back to the
              generic label only if no identity resolved — e.g. lookup
              failed server-side, or the agent's User record is
              missing the expected name fields). This is the actual
              fix for "the user should see the agent name." */}
          <p className="font-semibold text-sm truncate">
            {status === "with_agent"
              ? (conversation?.agent?.displayName || conversation?.agent?.email || "Support Agent")
              : "Easwallet Support"}
          </p>
          <p className="text-xs truncate" style={{ color: "var(--gradient-hero-text-muted)" }}>
            {status === "with_agent" ? "Agent connected" : status === "waiting_for_agent" ? "Finding an agent…" : "Here to help instantly"}
          </p>
        </div>

        {/* FIXED — trailing buttons grouped into one flex container
            with a single ml-auto, instead of "Talk to a human" and
            the close-X each independently claiming ml-auto (which
            collapsed them together rather than spacing them apart
            when both were present). */}
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          {status === "with_agent" && (
            <div className="flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full">
              <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
              <span className="text-xs font-medium">Live</span>
            </div>
          )}
          {/* {status === "ai" && (
            <button onClick={requestHuman} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap">
              👤 Talk to a human
            </button>
          )} */}
          {onClose && closeIcon === "x" && (
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          )}
        </div>
      </div>

      {opening && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-surface-muted">
          <div className="w-7 h-7 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-brand)", borderTopColor: "transparent" }} />
          <p className="text-secondary text-sm">Connecting…</p>
        </div>
      )}

      {!opening && status === "waiting_for_agent" && (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-surface-muted">
          <div className="relative mb-6">
            <div className="w-20 h-20 rounded-full flex items-center justify-center text-4xl" style={{ background: "var(--color-warning-light)" }}>⏳</div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full animate-pulse" style={{ background: "var(--color-warning)" }} />
          </div>
          <h3 className="text-lg font-bold text-primary mb-2 font-display">Looking for an agent</h3>
          <p className="text-secondary text-sm max-w-xs">We've got your conversation — an agent will pick up right where the AI left off.</p>
        </div>
      )}

      {!opening && (status === "ai" || status === "with_agent" || status === "closed") && (
        <>
          <div ref={chatContainerRef} className="flex-1 bg-surface-muted p-4 overflow-y-auto space-y-3">
            {messages.length === 0 && status === "ai" && (
              <div className="flex justify-center pt-8">
                <p className="text-muted text-sm text-center max-w-xs">👋 Hi! I'm here to help. Ask me anything, or tap "Talk to a human" anytime.</p>
              </div>
            )}
            {messages.map(renderMessage)}

            {aiThinking && status === "ai" && (
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: "var(--color-purple-light)", color: "var(--color-purple-text)" }}>🤖</div>
                <div className="bg-surface border border-border px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "var(--color-purple)", animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "var(--color-purple)", animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "var(--color-purple)", animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            {typing && status === "with_agent" && (
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: "var(--color-brand-light)", color: "var(--color-brand-text)" }}>A</div>
                <div className="bg-surface border border-border px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "var(--color-text-muted)", animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "var(--color-text-muted)", animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "var(--color-text-muted)", animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {status !== "closed" ? (
            <div className="bg-surface border-t border-border p-3 flex-shrink-0">
              {/* FIXED — visible upload error banner, dismissible.
                  This is the actual fix for "the user will not
                  know": uploadFile() now sets uploadError on any
                  failure (size limit, server rejection, network
                  error), and this renders it right where the user is
                  looking, instead of the failure only existing in a
                  console.error nobody but a developer would see. */}
              {uploadError && (
                <div className="mb-3 flex items-center gap-2 px-3 py-2.5 bg-danger-light border border-danger-border rounded-xl">
                  <span className="text-danger text-sm flex-shrink-0">⚠️</span>
                  <p className="text-danger-text text-xs flex-1">{uploadError}</p>
                  <button onClick={() => setUploadError(null)} className="text-danger-text text-xs font-semibold flex-shrink-0 hover:underline">
                    Dismiss
                  </button>
                </div>
              )}
              {audioPreviewUrl && (
                <div className="mb-3 p-3 bg-surface-muted border border-border-strong rounded-2xl">
                  <audio controls src={audioPreviewUrl} className="w-full mb-2 h-8" />
                  <div className="flex gap-2">
                    <button onClick={sendVoiceMessage} className="flex-1 bg-brand text-on-brand py-2.5 rounded-xl font-medium text-sm">Send ({recordingDuration}s)</button>
                    <button onClick={cancelRecording} className="flex-1 bg-surface-hover text-secondary py-2.5 rounded-xl font-medium text-sm">Discard</button>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-1.5 rounded-2xl px-2 py-1 border transition-colors"
                style={{ background: isRecording ? "var(--color-danger-light)" : "var(--color-surface-muted)", borderColor: isRecording ? "var(--color-danger-border)" : "var(--color-border-strong)" }}>
                <button onClick={() => fileInputRef.current.click()} className="w-9 h-9 hover:bg-surface-hover rounded-xl flex items-center justify-center text-secondary transition-colors">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                </button>
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileSelect} />
                <button onClick={toggleRecording} className="w-9 h-9 rounded-xl flex items-center justify-center transition-all"
                  style={{ background: isRecording ? "var(--color-danger)" : "transparent", color: isRecording ? "#fff" : "var(--color-text-secondary)", animation: isRecording ? "pulse 1.5s infinite" : "none" }}>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2H3v2a9 9 0 0 0 8 8.94V23h2v-2.06A9 9 0 0 0 21 12v-2h-2z"/></svg>
                </button>
                <input
                  type="text" value={messageInput} onChange={handleInputChange}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                  className="flex-1 bg-transparent outline-none px-2 py-2.5 text-sm text-primary placeholder:text-faint"
                  placeholder={isRecording ? `Recording… ${recordingDuration}s` : "Type a message…"}
                  disabled={isRecording}
                />
                <button onClick={sendMessage} disabled={!messageInput.trim() || isRecording} className="w-9 h-9 bg-brand disabled:bg-surface-hover disabled:text-faint text-on-brand rounded-xl flex items-center justify-center transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-surface border-t border-border p-4 flex-shrink-0 text-center">
              <p className="text-muted text-sm">This conversation has ended.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
